import { at } from 'lodash-es';
import { createCachedSelector } from 're-reselect';

import type { JobsJobElasticSchemaWithId } from '@iconik-io/iconik-api/src/services/jobs';
import type { ObjectType } from '@iconik-io/iconik-api/src/types/object_types';
import { FIELD_TYPES } from '@iconik-io/shared-lib/src/constants/search';

import {
  JOB_COMPLETED_STATUSES_SET,
  type JobColumnType,
  jobsStatusMap,
} from '../../constants/jobs';
import type { JobWidgetSchemaPatched } from '../../ducks/jobs_widgets/types';
import { useAppSelector } from '../../store/react_redux_typed';
import Label from '../../ui/label/label';
import ProgressPie from '../../ui/progress_pie/progress_pie';
import { MIN_COLUMN_WIDTH } from '../../ui/table/table';
import type { Column } from '../../ui/table/types';
import { HUMAN_OBJECT_TYPE_SINGULAR } from '../../utils/object_type';
import DebugDumpEntity from '../debug_dump_entity/debug_dump_entity';
import JobType from '../job_type/job_type';
import PriorityInput from './priority_input/priority_input';
import { StorageName } from './storage_name/storage_name';
import TitleButton from './title_button/title_button';
import { TranscoderName } from './transcoder_name/transcoder_name';

const menuColumn = {
  label: ' ',
  name: 'id',
  type: FIELD_TYPES.MENU,
  style: { width: MIN_COLUMN_WIDTH },
};

const progressColumn: Column<JobsJobElasticSchemaWithId> = {
  label: 'Progress',
  name: 'progress',
  type: FIELD_TYPES.CUSTOM_COMPONENT,
  component: ({ item }) => {
    if (item.progress === undefined) {
      return null;
    }
    let doneElement: string | undefined;
    if (item.progress >= 100 && JOB_COMPLETED_STATUSES_SET.has(item.status)) {
      doneElement = '';
    }
    return (
      <ProgressPie
        value={item.progress}
        doneElement={doneElement}
        variant={jobsStatusMap[item.status]?.bsStyle}
      />
    );
  },
  style: { width: 80 },
};

export const jobsColumns: Record<
  JobColumnType,
  Column<JobsJobElasticSchemaWithId>
> = {
  title: {
    name: 'title',
    label: 'Title',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    sortable: true,
    component: ({ item }) => (
      <>
        <DebugDumpEntity id={item.id} entityKey="jobs" />
        <TitleButton job={item} />
      </>
    ),
    style: { width: 'auto', minWidth: 100 },
  },
  created_by: {
    name: 'created_by',
    label: 'User',
    type: FIELD_TYPES.USER,
    sortable: true,
  },
  status: {
    name: 'status',
    label: 'Status',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    sortable: true,
    component: ({ item }) => {
      const status = jobsStatusMap[item.status];
      if (!status) {
        return null;
      }
      return <Label variant={status.bsStyle}>{status.label}</Label>;
    },
    style: { minWidth: 85 },
  },
  type: {
    name: 'type',
    label: 'Job Type',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    sortable: true,
    component: ({ item }) => {
      const transcoder = useAppSelector((state) =>
        item.metadata?.transcoder_id
          ? state.entities.transcoders[item.metadata?.transcoder_id as string]
          : undefined,
      );

      return (
        <>
          <JobType job={item} />
          {transcoder && (
            <>
              {'\u00A0('}
              <TranscoderName transcoderName={transcoder.name} />
              {')'}
            </>
          )}
        </>
      );
    },
    style: { maxWidth: 200 },
  },
  storage: {
    name: '_metadata.storage_id',
    label: 'Storage',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) => {
      if (!item.metadata?.storage_id) {
        return null;
      }
      return <StorageName storageId={item.metadata.storage_id as string} />;
    },
    style: { maxWidth: 200 },
  },
  date_created: {
    name: 'date_created',
    label: 'Created',
    type: FIELD_TYPES.DATETIME,
    sortable: true,
    style: { minWidth: 140 },
  },
  date_modified: {
    name: 'date_modified',
    label: 'Modified',
    type: FIELD_TYPES.DATETIME,
    sortable: true,
    style: { minWidth: 140 },
  },
  object_type: {
    name: 'object_type',
    label: 'Object type',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) =>
      item.object_type
        ? HUMAN_OBJECT_TYPE_SINGULAR[item.object_type as ObjectType]
        : null,
    sortable: true,
  },
  progress: progressColumn,
  progress_processed: progressColumn,
  priority: {
    name: 'priority',
    label: 'Priority',
    type: FIELD_TYPES.CUSTOM_COMPONENT,
    component: ({ item }) => {
      if (!item.action_context?.CHANGE_PRIORITY) {
        return null;
      }
      if (item.status === 'WAITING') {
        return <PriorityInput job={item} />;
      }
      return <div style={{ marginLeft: 30 }}>{item.priority}</div>;
    },
    style: { minWidth: 95 },
    sortable: true,
  },
};

const getColumns = (widget: JobWidgetSchemaPatched | undefined) =>
  widget?.options.columns;

export const selectJobsColumns = createCachedSelector(getColumns, (columns) => {
  if (!columns) {
    return [];
  }
  const result: Column<JobsJobElasticSchemaWithId>[] = at(
    jobsColumns,
    columns,
  ).filter((column) => column);
  if (result.length === 0 || !columns.includes('title')) {
    result.unshift(jobsColumns.title);
  }
  result.push(menuColumn);
  return result;
})((widget: JobWidgetSchemaPatched | undefined) => widget?.id);
